/**
 * Audit log — localStorage-based operation journal.
 * Allows investigation when a beta tester reports data loss.
 * Sentry captures JS errors; this captures successful AND failed operations.
 *
 * RULE: auditLog() NEVER breaks the main flow — entire body is try/catch.
 */

import { safeSetItem } from './safeLocalStorage';

export interface AuditEntry {
  timestamp: string;
  operation: 'create' | 'update' | 'delete' | 'save' | 'sync';
  target: string;
  targetId?: string;
  detail: string;
  success: boolean;
  error?: string;
}

const STORAGE_KEY = 'bigwa-audit-log';
const MAX_ENTRIES = 500;

export function auditLog(entry: Omit<AuditEntry, 'timestamp'>): void {
  try {
    const full: AuditEntry = {
      ...entry,
      timestamp: new Date().toISOString(),
    };

    const raw = localStorage.getItem(STORAGE_KEY);
    const entries: AuditEntry[] = raw ? JSON.parse(raw) : [];

    entries.push(full);

    // FIFO: keep only the most recent MAX_ENTRIES
    while (entries.length > MAX_ENTRIES) {
      entries.shift();
    }

    safeSetItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // Fail-safe: never break the main flow
    console.warn('[auditLog] failed to persist entry');
  }
}

export function getAuditLog(): AuditEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

export function clearAuditLog(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    console.warn('[auditLog] failed to clear');
  }
}
